import type { CaraWorkspaceFieldsFragment } from '~/api/graphql';
import { PdfPagesViewer } from '~/components/pdf-pages-viewer';
import { usePagedDocumentsEnabled } from '~/components/use-paged-documents-enabled';
import { VisibilityPopover } from '~/components/visibility/visibility-popover';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';
import { useArtifactEditor } from '~/components/workspaces/hooks/use-artifact-editor';
import { usePdfAttachmentExport } from '~/api/data/workspaces/use-pdf-attachment-export';
import { useWorkspaceAttachableArtifacts } from '~/components/workspaces/hooks/use-workspace-attachable-artifacts';
import {
  buildArtifactStaticsUrl,
  getArtifactTitle,
  type ArtifactStaticsKind
} from '~/components/workspaces/lib/artifact';
import { ArtifactFocusShell, ArtifactIframe } from './artifact-focus-shell';
import { OpenPdfWithAttachmentsButton } from './artifact-open-pdf-with-attachments-button';
import { useArtifactVersionNumber } from '~/components/workspaces/hooks/use-artifact-version-number';

/**
 * Artifact focus that shows the rendered pages (an iframe without the paged document), shared
 * by PDF and Custom Form artifacts.
 * The `kind` selects the statics URL path (`pdf` or `custom-form`).
 */
export const IframeArtifactFocus = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  artifact: CaraWorkspaceFieldsFragment['artifacts'][0];
  kind: Extract<ArtifactStaticsKind, 'pdf' | 'custom-form'>;
  onClose: () => void;
}) => {
  const artifactVersionNumber = useArtifactVersionNumber();
  const pagedDocuments = usePagedDocumentsEnabled();
  const title = getArtifactTitle(props.artifact);
  const src = buildArtifactStaticsUrl(
    props.kind,
    props.workspace.id,
    props.artifact.id,
    props.artifact.version
  );

  const { handleVisibilityUpdate } = useArtifactEditor({
    workspaceId: props.workspace.id,
    artifactId: props.artifact.id,
    version: props.artifact.version
  });
  const visibilityEditAllowed = useVisibilityEditAllowed(props.workspace.agencyUser?.id);

  const { workspaceFiles, workspaceMessageFiles, workspaceArtifacts } =
    useWorkspaceAttachableArtifacts(props.workspace);

  const showOpenPdf = props.kind === 'custom-form';

  const {
    prefixFileIds,
    suffixFileIds,
    setLocalFiles,
    attachmentCatalogList,
    onAttachmentChange,
    isOpening,
    openError,
    handleOpenPdf
  } = usePdfAttachmentExport({
    workspaceId: props.workspace.id,
    artifactId: props.artifact.id,
    version: props.artifact.version,
    workspaceFiles,
    workspaceMessageFiles,
    workspaceArtifacts
  });

  return (
    <ArtifactFocusShell
      title={title}
      version={artifactVersionNumber(props.artifact)}
      onClose={props.onClose}
      headerActions={
        <div className="flex items-center gap-2 flex-none">
          {showOpenPdf && (
            <OpenPdfWithAttachmentsButton
              onOpenPdf={handleOpenPdf}
              isOpening={isOpening}
              openError={openError}
              attachmentCatalog={attachmentCatalogList}
              prefixFileIds={prefixFileIds}
              suffixFileIds={suffixFileIds}
              onAttachmentChange={onAttachmentChange}
              onLocalFilesChange={setLocalFiles}
            />
          )}
          {visibilityEditAllowed && (
            <VisibilityPopover
              visibility={props.artifact.visibility}
              onSave={handleVisibilityUpdate}
            />
          )}
        </div>
      }
    >
      {pagedDocuments ? (
        <PdfPagesViewer src={src} title={title} className="mt-2" />
      ) : (
        <ArtifactIframe src={src} title={title} />
      )}
    </ArtifactFocusShell>
  );
};

/** @deprecated Prefer IframeArtifactFocus({ kind: 'pdf' }) directly from the dispatcher. */
export const PdfArtifactFocus = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  artifact: CaraWorkspaceFieldsFragment['artifacts'][0];
  onClose: () => void;
}) => <IframeArtifactFocus {...props} kind="pdf" />;

/** @deprecated Prefer IframeArtifactFocus({ kind: 'custom-form' }) directly from the dispatcher. */
export const CustomFormFocus = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  artifact: CaraWorkspaceFieldsFragment['artifacts'][0];
  onClose: () => void;
}) => <IframeArtifactFocus {...props} kind="custom-form" />;
